import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { FileText } from 'lucide-react';
import { CONTACT_EMAIL, CONTACT_LOCATION } from '../config/legal';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#00D47E]/10 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#00D47E]" />
            </div>
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-black">Terms of Service</h1>
          </div>
          <p className="text-gray-500 text-sm mb-12">Last updated: August 2026</p>

          <div className="space-y-10 text-gray-700 leading-relaxed">
            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">1. About Levr</h2>
              <p>
                Levr ("we", "us", "our") operates an online vehicle finance origination platform at levr.nz. We provide an eligibility assessment tool that estimates your likelihood of approval for vehicle and personal finance from New Zealand lenders. When you choose to proceed, our finance specialists review your application and work with lenders on your behalf.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">2. Eligibility Assessment</h2>
              <p className="mb-3">
                Our eligibility tool provides an <strong>indicative assessment only</strong>. It is not a credit decision, a guarantee of approval, or a formal offer of finance. The assessment is based on the information you provide and general NZ lending criteria.
              </p>
              <p>
                Your eligibility result, approval likelihood score, and estimated repayment figures are indicative only and may differ from actual lender offers. Final approval, rates, and terms are determined solely by the lender you apply with, based on their own assessment criteria including credit checks.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">3. No Credit Check During Assessment</h2>
              <p>
                Our eligibility assessment does not perform a credit check and does not affect your credit score. A credit check will only be conducted by a lender if you choose to proceed with a formal application and the lender requires one as part of their approval process. We will make this clear before any such step.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">4. Our Role as a Broker</h2>
              <p>
                Levr acts as a finance broker connecting you with licensed NZ lenders. We do not lend money directly. When you submit an application through Levr, our finance specialists review your details and work with appropriate lenders on your behalf. The lending relationship is between you and the lender, and is governed by that lender's own terms and conditions.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">5. Accuracy of Information</h2>
              <p>
                You agree to provide accurate and complete information when using our services. Providing false or misleading information may result in your application being declined by lenders and could constitute fraud.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">6. Document Upload and AI Processing</h2>
              <p>
                The online eligibility check does not ask you to upload anything. Where a step does ask for documents, this applies: bank statements and ID documents are processed using AI to extract relevant information such as income and identity details, and a selfie may be captured for identity verification. Documents are processed in memory and are not permanently stored as files. By uploading documents, you consent to this processing. See our <a href="/privacy" className="text-[#00D47E] hover:underline">Privacy Policy</a> for details on how your data is handled.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">7. Indicative Rates and Payments</h2>
              <p>
                All rates, weekly payments, and repayment estimates shown on our platform are indicative only and are based on general lender criteria. Actual rates offered to you may be higher or lower depending on your credit history, the lender's assessment, and current market conditions. We do not guarantee any specific rate or outcome.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">8. Dealer Referrals</h2>
              <p>
                If you were referred to Levr by a vehicle dealer, the dealer may receive a commission from us if your application results in a funded loan. This does not affect the rates or terms offered to you by lenders.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">9. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, Levr is not liable for any loss or damage arising from your use of our eligibility tool, the accuracy of our assessments, or the outcome of any finance application. Our service is provided "as is" and we make no warranties about the availability, accuracy, or completeness of information provided.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">10. Changes to These Terms</h2>
              <p>
                We may update these terms from time to time. Continued use of our platform after changes are posted constitutes acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">11. Governing Law</h2>
              <p>
                These terms are governed by the laws of New Zealand. Any disputes arising from the use of our services are subject to the exclusive jurisdiction of the courts of New Zealand.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-black mb-3">12. Contact</h2>
              <p>
                If you have questions about these terms, contact us at:
              </p>
              <p className="mt-2">
                <strong>Email:</strong> {CONTACT_EMAIL}<br />
                <strong>Location:</strong> {CONTACT_LOCATION}
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;
